<template>
  <v-card class="mt-6">
    <v-card-title>
      <span class="text-h5">Générer et poster l'article sur WordPress</span>
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="12">
          <v-alert v-if="!hasAllRequiredContent" type="warning" outlined>
            <strong>Informations manquantes</strong>
            <ul class="mt-2 pl-5">
              <li>Angle (topic) : {{ hasTopic ? "✅" : "❌" }}</li>
              <li>
                Longueur du {{ topicIsTitle ? "titre" : "topic" }} :
                {{ topicCharacterCount }}/{{ topicCharacterLimit }}
                {{ topicWithinLimit ? "✅" : "❌" }}
              </li>
              <li>
                Au moins une source : {{ sourceRequirementMet ? "✅" : "❌" }}
              </li>
            </ul>
            <div class="mt-2">
              Veuillez compléter les informations ci-dessus pour continuer.
            </div>
          </v-alert>
          <v-col cols="12" v-else>
            <v-alert type="info" outlined dense class="mb-4">
              <strong>Prêt à publier</strong> - Topic et sources valides.
            </v-alert>
          </v-col>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="selectedSite"
            :items="sites"
            item-text="site.name"
            item-value="site.id"
            label="Site sélectionné"
            outlined
            dense
            hide-no-data
            clearable
            return-object
          >
            <template v-slot:item="{ item }">
              <v-list-item-avatar color="grey lighten-2">
                <v-icon fab color="blue-grey darken-2">{{
                  icons.mdiWordpress
                }}</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title
                  v-text="
                    item && item.site && item.site.name
                      ? item.site.name
                      : 'Site inconnu'
                  "
                ></v-list-item-title>
                <v-list-item-subtitle
                  v-text="item && item.site_url ? item.site_url : ''"
                ></v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="2">
          <v-select
            v-model="postStatus"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            label="Statut"
            outlined
            dense
            :disabled="isScheduled"
          ></v-select>
        </v-col>

        <v-col cols="12" md="6" class="d-flex justify-center">
          <aif-image-mode-toggle v-model="imageHandler" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="5">
            <custom-prompt-template-filter
              :noUrl="true"
              :forceSiteId="Number(siteId || 0)"
              store="aiFactory"
              preselect-default
              dense
            />
        </v-col>
        <v-col cols="12" md="5">
          <forced-category-filter
            :forceSiteId="Number(siteId || 0)"
            :initialValue="forcedCategoryId"
            dense
            @valueChanged="forcedCategoryId = $event"
          />
        </v-col>
      </v-row>

      <v-row>

        <v-col cols="12" md="6">
          <v-checkbox
            v-model="isScheduled"
            :label="scheduledLabel"
          ></v-checkbox>
        </v-col>

        <v-col cols="12" md="4" v-if="isScheduled">
          <v-menu
            ref="scheduleMenu"
            v-model="scheduleMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedScheduledDateTime"
                label="Date et heure de publication"
                prepend-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-bind="attrs"
                v-on="on"
                :error-messages="
                  isScheduled && !scheduledAtISO ? 'Date et heure requises' : ''
                "
              ></v-text-field>
            </template>

            <v-card>
              <v-date-picker
                v-model="scheduledDate"
                :min="minDate"
                locale="fr-fr"
                no-title
                scrollable
              ></v-date-picker>

              <v-divider></v-divider>
              <v-time-picker
                v-if="scheduleMenu"
                v-model="scheduledTime"
                :min="minTime"
                format="24hr"
                full-width
                scrollable
              ></v-time-picker>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="scheduleMenu = false">
                  OK
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-col>

        <v-col cols="12">
          <v-row align="center" no-gutters>
            <v-col cols="12" md="10">
              <v-btn
                color="primary"
                @click="addToPostGenerationQueue"
                :loading="isLoading"
                :disabled="!hasAllRequiredContent || !siteId"
                block
              >
                Envoyer la demande de génération d'article
              </v-btn>
            </v-col>

            <v-col cols="12" md="2">
              <v-checkbox
                v-model="stayOnPage"
                label="Rester sur la page"
                class="ml-6 pt-0"
                hide-details
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" v-if="publishedUrl">
          <v-alert type="success" text>
            <strong>Demande effectuée avec succès !</strong>
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import store from "@/store";
import { mapActions, mapGetters } from "vuex";
import { mdiCalendar, mdiWordpress } from "@mdi/js";
import AifImageModeToggle from "@/components/ai-factory/common/AifImageModeToggle.vue";
import CustomPromptTemplateFilter from "@/components/ai-factory/full-auto/filters/CustomPromptTemplateFilter.vue";
import ForcedCategoryFilter from "@/components/ai-factory/full-auto/filters/ForcedCategoryFilter.vue";

export default {
  name: "WordPressPublisher",
  components: {
    AifImageModeToggle,
    CustomPromptTemplateFilter,
    ForcedCategoryFilter,
  },

  props: {
    siteId: {
      type: [Number, String],
      default: null,
    },
    sites: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectedSite: null, // Site sélectionné
      postStatus: "draft",
      isLoading: false,
      publishedUrl: null,
      forcedCategoryId: null,
      statusOptions: [
        { text: "Brouillon", value: "draft" },
        { text: "Publié", value: "publish" },
        // { text: "En attente", value: "pending" },
        // { text: "Privé", value: "private" },
      ],
      isScheduled: false,
      scheduleMenu: false,
      activePickerTab: 0,
      scheduledDate: null,
      scheduledTime: null,
      scheduleTimeBufferMinutes: 1,
      stayOnPage: false,
      icons: {
        mdiWordpress,
        mdiCalendar,
      },
    };
  },
  watch: {
    // Site selection watcher - now uses siteId prop
    async siteId(newVal) {
      const itemId = this.$route.params.id; // On récupère l'ID depuis l'URL
      // si mode creation
      if (!itemId) {
        if (newVal) {
          await this.loadSiteConfiguration(newVal);
        }
      }

      // Initialiser le selectedSite avec le siteId reçu via la prop
      this.updateSelectedSiteFromProps();
    },
    sites: {
      handler() {
        // Mettre à jour selectedSite lorsque la liste des sites change
        this.updateSelectedSiteFromProps();
      },
      immediate: true,
    },
    selectedSite(newVal) {
      if (newVal && newVal.site && newVal.site.id) {
        this.$emit("update:siteId", newVal.site.id);
      }
    },
    isScheduled(isNowScheduled) {
      // When scheduling is enabled, force postStatus to "future" — the user cannot
      // choose another status because scheduled articles always go to WP as "future".
      // Reset to "draft" when scheduling is disabled.
      this.postStatus = isNowScheduled ? "future" : "draft";
      if (isNowScheduled && !this.scheduledAtISO) {
        const defaultDateTime = new Date();

        // ANCIENNE LIGNE :
        // defaultDateTime.setHours(defaultDateTime.getHours() + 1);

        // NOUVELLE LIGNE :
        defaultDateTime.setMinutes(
          defaultDateTime.getMinutes() + this.scheduleTimeBufferMinutes
        );

        this.scheduledDate = defaultDateTime.toISOString().substr(0, 10);

        const hours = String(defaultDateTime.getHours()).padStart(2, "0");
        const minutes = String(defaultDateTime.getMinutes()).padStart(2, "0");
        this.scheduledTime = `${hours}:${minutes}`;
      } else if (!isNowScheduled) {
        this.scheduledDate = null;
        this.scheduledTime = null;
      }
    },
  },

  computed: {
    ...mapGetters("aiFactoryTmp", [
      "getTopic",
      "getTopicIsTitle",
      "getSources",
      "getAllowAutoScrape",
      "getImageHandler",
    ]),
    ...mapGetters("aiFactory", ["getCustomPromptTemplate"]),

    imageHandler: {
      get() {
        return this.getImageHandler;
      },
      set(value) {
        this.updateImageHandler(value);
      },
    },

    hasTopic() {
      return this.getTopic && this.getTopic.trim().length > 0;
    },

    topicCharacterCount() {
      return Array.from(this.getTopic || "").length;
    },

    topicCharacterLimit() {
      return this.topicIsTitle ? 250 : 255;
    },

    topicWithinLimit() {
      return this.topicCharacterCount <= this.topicCharacterLimit;
    },

    hasSelectedSite() {
      return !!this.siteId;
    },

    selectedSite: {
      get() {
        if (!this.siteId) {
          return null;
        }

        if (!Array.isArray(this.sites) || this.sites.length === 0) {
          return null;
        }

        const site = this.sites.find(
          (s) => s && s.site && s.site.id === this.siteId
        );

        return site || null;
      },
      set(value) {
        this.selectedSite = value;
      },
    },

    siteName() {
      if (!this.selectedSite) {
        return "Aucun site sélectionné";
      }

      return this.selectedSite &&
        this.selectedSite.site &&
        this.selectedSite.site.name
        ? this.selectedSite.site.name
        : "Aucun site sélectionné";
    },

    topicIsTitle() {
      return this.getTopicIsTitle;
    },

    hasSourcesText() {
      return (
        this.getSources &&
        this.getSources.some(
          (s) => s && typeof s === "object" && s.content && s.content.trim()
        )
      );
    },

    hasSourceUrl() {
      if (!this.getSources) return false;
      return this.getSources.some((s) => {
        if (s && typeof s === "object") return s.url && s.url.trim() !== "";
        if (typeof s === "string") return s.trim() !== "";
        return false;
      });
    },

    // Vérifie si au moins un contenu est présent
    hasSourceContent() {
      if (!this.getSources) return false;
      return this.getSources.some(
        (s) => s && typeof s === "object" && s.content && s.content.trim()
      );
    },

    // logique principale
    // sourceRequirementMet() {
    //   if (this.allowAutoScrape) {
    //     // Si le scraping auto est activé, il faut au moins une URL non vide.
    //     return this.hasSourceUrl;
    //   } else {
    //     // Sinon (scraping manuel), il faut au moins un contenu non vide (après scraping).
    //     return this.hasSourceContent;
    //   }
    // },
    sourceRequirementMet() {
      // Si du contenu manuel est présent, la condition est TOUJOURS remplie
      // (permet d'envoyer du texte seul, même sans URL).
      if (this.hasSourceContent) {
        return true;
      }
      // Sinon (pas de contenu), si l'auto-scrape est activé,
      // on exige alors au moins une URL.
      if (this.allowAutoScrape) {
        return this.hasSourceUrl;
      }
      // Si auto-scrape est OFF et qu'il n'y a ni contenu ni URL, c'est false.
      return false;
    },

    // logique de validation du bouton
    hasAllRequiredContent() {
      return (
        this.hasTopic && this.topicWithinLimit && this.sourceRequirementMet
      );
    },

    allowAutoScrape() {
      return this.getAllowAutoScrape;
    },

    scheduledLabel() {
      if (this.postStatus === "draft") {
        return "Programmer la création de l'article et l'envoi en brouillon sur le backoffice du site";
      }
      return "Programmer la création de l'article et la publication en ligne";
    },

    minDate() {
      const today = new Date();
      return today.toISOString().substr(0, 10);
    },

    minTime() {
      // Pas de restriction si une date future est sélectionnée
      if (this.scheduledDate !== this.minDate) {
        return null;
      }

      // Calcule maintenant + 1 heure
      const minDateTime = new Date();
      minDateTime.setMinutes(
        minDateTime.getMinutes() + this.scheduleTimeBufferMinutes
      );

      const hours = String(minDateTime.getHours()).padStart(2, "0");
      const minutes = String(minDateTime.getMinutes()).padStart(2, "0");

      return `${hours}:${minutes}`;
    },

    formattedScheduledDateTime() {
      if (!this.scheduledDate || !this.scheduledTime) {
        return null;
      }
      const [year, month, day] = this.scheduledDate.split("-");
      return `Le ${day}/${month}/${year} à ${this.scheduledTime}`;
    },

    scheduledAtISO() {
      if (!this.scheduledDate || !this.scheduledTime) {
        return null;
      }

      // 1. On combine la date et l'heure pour former une chaîne ISO locale.
      //    Exemple: "2025-10-29T14:00:00"
      const localDateTimeString = `${this.scheduledDate}T${this.scheduledTime}:00`;

      // 2. On crée un objet Date à partir de cette chaîne.
      //    Important : En l'absence de 'Z', new Date() interprète la
      //    chaîne comme étant dans le FUSEAU HORAIRE LOCAL du navigateur.
      //    Donc, si l'utilisateur est à Paris, l'objet `localDate`
      //    représente "le 29 octobre à 14h00 (heure de Paris)".
      const localDate = new Date(localDateTimeString);

      // 3. On utilise .toISOString() pour obtenir la représentation UTC
      //    de cet instant.
      //    Ex: "14h00 heure de Paris" (CET, UTC+1) deviendra
      //    "2025-10-29T13:00:00.000Z"
      return localDate.toISOString();
    },
  },

  mounted() {
    // Site loading moved to main page
    const itemId = this.$route.params.id; // On récupère l'ID depuis l'URL
    if (!itemId) {
      this.loadSiteConfiguration(this.siteId);
    }

    // Si aucun site n'est sélectionné, sélectionner le premier site par défaut
    if (!this.siteId && this.sites.length > 0) {
      this.selectedSite = this.sites[0];
    }
  },

  methods: {
    ...mapActions("aiFactoryTmp", [
      "sendToPostGenerationQueue",
      "updateImageHandler",
      "fetchConnectors",
      "updateTopic",
      "updateSources",
      "updateTopicIsTitle",
    ]),

    updateSelectedSiteFromProps() {
      if (this.siteId && Array.isArray(this.sites) && this.sites.length > 0) {
        const site = this.sites.find(
          (s) => s && s.site && s.site.id === this.siteId
        );
        if (site) {
          this.selectedSite = site;
        } else if (!this.selectedSite) {
          // Si le site n'est pas trouvé et qu'aucun site n'est sélectionné,
          // sélectionner le premier site par défaut
          this.selectedSite = this.sites[0];
        }
      } else if (
        !this.selectedSite &&
        Array.isArray(this.sites) &&
        this.sites.length > 0
      ) {
        // Si aucun site n'est sélectionné et que la liste des sites est disponible,
        // sélectionner le premier site par défaut
        this.selectedSite = this.sites[0];
      }
    },

    async loadSiteConfiguration(siteId) {
      // On ne deduit plus le handler de config.image_model : ce champ n'est
      // rempli que si le template du site porte un prompt image propre, donc il
      // valait null pour tout template recent -> l'image etait silencieusement
      // desactivee a chaque changement de site.
      // Un prompt image se resout TOUJOURS (template -> site -> global), donc
      // "generate" est le defaut correct ; le toggle laisse l'utilisateur dire
      // non pour un article donne.
      this.updateImageHandler("generate");
      console.log(`Site config loaded for ${siteId}: image handler = generate`);
    },

    async addToPostGenerationQueue() {
      if (!this.topicWithinLimit) {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Le ${
            this.topicIsTitle ? "titre définitif" : "topic"
          } ne peut pas dépasser ${this.topicCharacterLimit} caractères.`,
          color: "error",
          timeout: 5000,
        });
        return;
      }

      if (!this.hasAllRequiredContent) {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: "Veuillez fournir un angle et au moins une source avant de continuer.",
          color: "error",
          timeout: 5000,
        });
        return;
      }

      if (this.isScheduled && !this.scheduledAtISO) {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: "Veuillez sélectionner une date et une heure de programmation valides.",
          color: "error",
          timeout: 5000,
        });
        return;
      }

      this.isLoading = true;

      try {
        // récupération de l'email utilisateur dans le localStorage
        const userEmail = localStorage.getItem("userEmail");

        const filteredSources = this.getSources.filter((s) => {
          if (typeof s === "string") {
            return s.trim() !== "";
          }
          if (typeof s === "object" && s !== null) {
            const hasUrl = s.url && s.url.trim() !== "";
            const hasContent = s.content && s.content.trim() !== "";
            return hasUrl || hasContent;
          }
          return false; // Ignore les entrées null ou invalides
        });

        const payload = {
          siteId: this.siteId,
          status: this.postStatus,
          topic: this.getTopic,
          topicIsTitle: this.topicIsTitle,
          aifCustomPromptTemplateId: this.getCustomPromptTemplate || null,
          sources: filteredSources,
          userEmail: userEmail || null,
          imageHandler: this.imageHandler,
          allowAutoScrape: this.allowAutoScrape,
          rmrOriginPageId: 90, // # rmr_origin_page -> Via Angles (meta_option)
          forcedCategoryId: this.forcedCategoryId,
        };

        if (this.isScheduled && this.scheduledAtISO) {
          payload.scheduled = this.scheduledAtISO;
        }

        console.log("addToPostGenerationQueue payload", payload);

        const result = await this.sendToPostGenerationQueue(payload);
        console.log("result", result);

        if (result.success) {
          await store.dispatch("app/setSnackBar", {
            show: true,
            text: `La demande de génération de l'article a bien été prise en compte. ID: ${result.queue_id}`,
            color: "success",
            timeout: 2000,
          });
          if (this.stayOnPage) {
            // Réinitialiser les champs comme demandé
            this.updateTopic("");
            this.updateTopicIsTitle(false);
            this.updateSources([{ url: "", content: "" }]);
            // On peut aussi remonter en haut de la page
            window.scrollTo(0, 0);
          } else {
            // Comportement original : redirection avec filtre Site pré-rempli
            this.$router.push({
              name: "ai-factory-post-generation-list",
              query: this.siteId ? { site_id: this.siteId } : {},
            });
          }
        } else {
          await store.dispatch("app/setSnackBar", {
            show: true,
            text: `Échec de la demande`,
            color: "error",
            timeout: 3000,
          });
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
<style>
.v-list-item {
  padding: 25px 5px;
}
</style>
